import { motion } from 'framer-motion';
import { Phone, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Team = () => {
  const teamMembers = [
    {
      name: 'Sergio Morgado',
      role: 'CEO',
      phone: '+971 55 881 9783',
      image: 'https://customer-assets.emergentagent.com/job_samrelocation/artifacts/wkjilbx9_image.png',
      bio: 'With over 15 years of experience in international business development and corporate strategy, Sergio leads SAM Relocation Group with a vision to create seamless business setup and relocation experiences in the UAE.',
      specialties: ['Business Strategy', 'International Expansion', 'Corporate Leadership'],
      color: '#3498DB'
    },
    {
      name: 'Mouna Kallel',
      role: 'CFO - International Finance & Tax Consultant',
      phone: '+971 504 593 101',
      image: 'https://customer-assets.emergentagent.com/job_samrelocation/artifacts/fzwztwq6_image.png',
      bio: 'Mouna brings extensive expertise in international taxation, financial planning, and regulatory compliance. She ensures clients benefit from optimal financial structuring and full compliance with UAE regulations.',
      specialties: ['International Tax', 'Financial Planning', 'Corporate Tax (Registration/Filing)', 'VAT (Registration/Filing)', 'Accounting', 'Auditing', 'Finance', 'Customer Service'],
      color: '#27AE60'
    },
    {
      name: 'José Canto Moreira',
      role: 'Director',
      image: 'https://customer-assets.emergentagent.com/job_samrelocation/artifacts/mwjouotn_image.png',
      bio: 'José oversees operations and ensures the highest quality of service delivery across all SAM divisions. With a proven track record in business operations and client relationship management.',
      specialties: ['Operations Management', 'Client Relations', 'Business Development'],
      color: '#5DADE2'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="team-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center text-white pt-20" data-testid="team-hero-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920" 
            alt="Team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#5DADE2]" />
              <span className="text-[#5DADE2] font-semibold tracking-[0.2em] uppercase text-sm">Our Team</span>
              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#5DADE2]" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-testid="team-hero-title">
              Meet Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5DADE2] to-[#27AE60]">Expert Team</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experienced professionals dedicated to your success in the UAE
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="team-members-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="team-section-heading">
              Leadership <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">Team</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              A team of seasoned professionals with diverse expertise and a shared commitment to excellence
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 hover:border-white/20 transition-all duration-500"
                data-testid={`team-member-${index}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-5">
                  {/* Image */}
                  <div className="md:col-span-2 relative overflow-hidden">
                    <motion.img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-64 md:h-full object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.5 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0a0a] hidden md:block" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent md:hidden" />
                  </div>
                  
                  {/* Content */}
                  <div className="md:col-span-3 p-8">
                    <h3 className="text-2xl font-bold text-white mb-2">{member.name}</h3>
                    <p className="font-medium mb-2" style={{ color: member.color }}>{member.role}</p>
                    
                    {member.phone && (
                      <a 
                        href={`tel:${member.phone.replace(/\s/g, '')}`}
                        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm mb-4"
                      >
                        <Phone size={14} />
                        {member.phone}
                      </a>
                    )}
                    
                    <p className="text-gray-400 leading-relaxed mb-6 text-sm">{member.bio}</p>
                    
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Specialties</p>
                      <div className="flex flex-wrap gap-2">
                        {member.specialties.slice(0, 4).map((specialty, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 text-xs rounded-full border border-white/10 text-gray-400"
                          >
                            {specialty}
                          </span>
                        ))}
                        {member.specialties.length > 4 && (
                          <span className="px-3 py-1 text-xs rounded-full border border-white/10 text-gray-400">
                            +{member.specialties.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Accent line */}
                <div 
                  className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500"
                  style={{ background: `linear-gradient(90deg, ${member.color}, transparent)` }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-testid="team-cta-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] to-[#111]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3498DB] rounded-full filter blur-[200px] opacity-10" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#27AE60] rounded-full filter blur-[200px] opacity-10" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-testid="team-cta-heading">
              Work With Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5DADE2] to-[#27AE60]">Expert Team</span>
            </h2>
            <p className="text-xl text-gray-400 mb-10 leading-relaxed">
              Ready to benefit from our team's expertise? Get in touch today.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-bold text-lg rounded-full hover:shadow-[0_0_40px_rgba(52,152,219,0.5)] transition-all duration-300"
              data-testid="team-cta-button"
            >
              Contact Us
              <ArrowRight size={22} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Team;
